import React, { FC, memo, useEffect, useRef, useState } from 'react';
import { MdSettings } from 'react-icons/md';
import axios from '../../../../axios';
import { playerService } from '../../../../services/player';
import { msToTime } from '../../../../utils';
import { Slider } from '../../../Slider';
import { Pause, Play } from '../../../Icons';
import './MobileNowPlayingModal.scss';

interface LyricLine {
  timeMs: number | null;
  text: string;
  romanizedText?: string;
}

interface LyricsPayload {
  synced: boolean;
  hasRomanized?: boolean;
  lines: LyricLine[];
}

const lyricsCache: Record<string, LyricsPayload> = {};

const EqualizerIcon = () => (
  <svg
    width="14"
    height="14"
    viewBox="0 0 24 24"
    fill="currentColor"
    aria-hidden="true"
    style={{ display: 'inline-block', flexShrink: 0 }}
  >
    <rect x="2" y="8" width="3" height="8" rx="1.5" />
    <rect x="8" y="3" width="3" height="18" rx="1.5" />
    <rect x="14" y="6" width="3" height="12" rx="1.5" />
    <rect x="20" y="9" width="3" height="6" rx="1.5" />
  </svg>
);

interface MobileLyricsCardProps {
  song: Spotify.Track;
  position: number;
  duration: number;
  paused: boolean;
  dominantColor: string;
}

export const MobileLyricsCard: FC<MobileLyricsCardProps> = memo(({
  song,
  position,
  duration,
  paused,
  dominantColor,
}) => {
  const [lyricsData, setLyricsData] = useState<LyricsPayload | null>(null);
  const [loading, setLoading] = useState(false);
  const [isFullScreen, setIsFullScreen] = useState(false);
  const [sliderValue, setSliderValue] = useState<number>(0);
  const [isSeeking, setIsSeeking] = useState<boolean>(false);
  const [showRomanized, setShowRomanized] = useState<boolean>(() => {
    const saved = localStorage.getItem('lyrics_prefer_romanized');
    return saved !== null ? saved === 'true' : true;
  });
  const [followLyrics, setFollowLyrics] = useState<boolean>(() => {
    const saved = localStorage.getItem('lyrics_sync_enabled');
    return saved !== null ? saved === 'true' : true;
  });
  const [showSettings, setShowSettings] = useState<boolean>(false);
  const [isUserScrolled, setIsUserScrolled] = useState<boolean>(false);

  const fullScreenContainerRef = useRef<HTMLDivElement>(null);
  const activeLineRef = useRef<HTMLParagraphElement>(null);
  const settingsRef = useRef<HTMLDivElement>(null);
  const isProgrammaticScrollRef = useRef(false);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent | TouchEvent) => {
      if (settingsRef.current && !settingsRef.current.contains(e.target as Node)) {
        setShowSettings(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('touchstart', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
    };
  }, []);

  // Sync seekbar in fullscreen
  useEffect(() => {
    if (!isSeeking && duration > 0) {
      setSliderValue(position / duration);
    }
  }, [position, duration, isSeeking]);

  // Fetch lyrics
  useEffect(() => {
    const songId = song?.id;
    if (!songId) return;

    isProgrammaticScrollRef.current = true;
    setIsUserScrolled(false);
    setTimeout(() => {
      isProgrammaticScrollRef.current = false;
    }, 150);
    if (lyricsCache[songId]) {
      setLyricsData(lyricsCache[songId]);
      return;
    }

    setLoading(true);
    const controller = new AbortController();

    axios
      .get(`/songs/${songId}/lyrics`, { signal: controller.signal })
      .then((res: any) => {
        const data = res.data?.data;
        if (data?.lrcData && data.lrcData.length > 0) {
          const parsed: LyricLine[] = data.lrcData.map((line: any) => ({
            timeMs: line.time !== null && line.time !== undefined ? Math.floor(line.time * 1000) : null,
            text: line.text || ' ',
            romanizedText: line.romanizedText || undefined,
          }));
          const result: LyricsPayload = { 
            synced: !!data.syncedLyrics && parsed.some(l => l.timeMs !== null), 
            hasRomanized: !!data.hasRomanized, 
            lines: parsed 
          };
          lyricsCache[songId] = result;
          setLyricsData(result);
        } else if (data?.lyrics) {
          const lines = data.lyrics
            .split('\n')
            .map((l: string) => l.trim())
            .filter((l: string) => l.length > 0);
          const romanLines = data.romanizedLyrics
            ? data.romanizedLyrics.split('\n').map((l: string) => l.trim()).filter((l: string) => l.length > 0)
            : [];
          const result: LyricsPayload = {
            synced: false,
            hasRomanized: !!data.hasRomanized && romanLines.length > 0,
            lines: lines.map((text: string, idx: number) => ({
              timeMs: null,
              text,
              romanizedText: romanLines[idx] || undefined
            })),
          };
          lyricsCache[songId] = result;
          setLyricsData(result);
        } else {
          setLyricsData(null);
        }
      })
      .catch(() => {
        setLyricsData(null);
      })
      .finally(() => setLoading(false));

    return () => {
      controller.abort();
    };
  }, [song?.id]);

  // Compute active lyric line
  let activeIndex = -1;
  if (lyricsData?.lines && lyricsData.lines.length > 0) {
    if (lyricsData.synced) {
      for (let i = 0; i < lyricsData.lines.length; i++) {
        const lineTime = lyricsData.lines[i].timeMs;
        if (typeof lineTime === 'number' && !isNaN(lineTime)) {
          if (lineTime <= position) {
            activeIndex = i;
          } else {
            break;
          }
        }
      }
    }
  }

  // Auto-scroll in fullscreen view
  useEffect(() => {
    if (isFullScreen && followLyrics && !isUserScrolled && activeLineRef.current && fullScreenContainerRef.current) {
      isProgrammaticScrollRef.current = true;
      activeLineRef.current.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
      });
      const timer = setTimeout(() => {
        isProgrammaticScrollRef.current = false;
      }, 400);
      return () => clearTimeout(timer);
    }
  }, [activeIndex, isFullScreen, followLyrics, isUserScrolled]);

  const handleScroll = () => {
    if (isProgrammaticScrollRef.current) return;
    if (followLyrics && lyricsData?.synced && !isUserScrolled) {
      setIsUserScrolled(true);
    }
  };

  if (!lyricsData || !lyricsData.lines || lyricsData.lines.length === 0) {
    return null;
  }

  const handleSeekEnd = (val: number | null | undefined) => {
    setIsSeeking(false);
    if (val === null || val === undefined || isNaN(val)) return;
    setSliderValue(val);
    const newPos = Math.round(val * duration);
    playerService.seekToPosition(newPos).then();
  };

  const handleShare = (e?: React.MouseEvent) => {
    e?.stopPropagation();
    if (navigator.share) {
      navigator
        .share({
          title: song.name,
          text: `Lyrics for ${song.name} on OpenMusic`,
          url: window.location.href,
        })
        .catch(() => {});
    }
  };

  // Preview lines: 4 lines around activeIndex
  const startPreview = activeIndex >= 0 ? Math.max(0, Math.min(activeIndex, lyricsData.lines.length - 4)) : 0;
  const previewLines = lyricsData.lines.slice(startPreview, startPreview + 4);

  return (
    <>
      {/* 1. Lyrics Preview Card */}
      <div
        className='mnp-lyrics-card'
        style={{
          background: `linear-gradient(145deg, ${dominantColor}cc, #1e1e1ecc)`,
        }}
        onClick={() => setIsFullScreen(true)}
      >
        <div className='mnp-lyrics-card-header'>
          <h2 className='mnp-lyrics-card-title'>Lyrics</h2>
          <div className='mnp-lyrics-card-actions'>
            <button
              className='mnp-lyrics-action-btn'
              onClick={handleShare}
              aria-label='Share lyrics'
            >
              <svg role='img' height='18' width='18' viewBox='0 0 24 24' fill='#ffffff'>
                <path d='M18 16.08c-.76 0-1.44.3-1.96.77L8.91 12.7c.05-.23.09-.46.09-.7s-.04-.47-.09-.7l7.05-4.11c.54.5 1.25.81 2.04.81 1.66 0 3-1.34 3-3s-1.34-3-3-3-3 1.34-3 3c0 .24.04.47.09.7L8.04 9.81C7.5 9.31 6.79 9 6 9c-1.66 0-3 1.34-3 3s1.34 3 3 3c.79 0 1.5-.31 2.04-.81l7.12 4.16c-.05.21-.08.43-.08.65 0 1.61 1.31 2.92 2.92 2.92s2.92-1.31 2.92-2.92c0-1.61-1.31-2.92-2.92-2.92z' />
              </svg>
            </button>

            <button
              className='mnp-lyrics-action-btn'
              onClick={(e) => {
                e.stopPropagation();
                setIsFullScreen(true);
              }}
              aria-label='Expand lyrics'
            >
              <svg role='img' height='18' width='18' viewBox='0 0 24 24' fill='#ffffff'>
                <path d='M3 3h8v2H5v6H3V3zm18 0v8h-2V5h-6V3h8zm0 18h-8v-2h6v-6h2v8zM3 21v-8h2v6h6v2H3z' />
              </svg>
            </button>
          </div>
        </div>

        <div className='mnp-lyrics-preview-lines'>
          {previewLines.map((line, idx) => {
            const actualIndex = startPreview + idx;
            const isCurrent = actualIndex === activeIndex;
            const displayText = (showRomanized && line.romanizedText) ? line.romanizedText : line.text;
            return (
              <p
                key={actualIndex}
                className={`mnp-lyrics-line ${isCurrent ? 'active' : ''}`}
              >
                {displayText}
              </p>
            );
          })}
        </div>
      </div>

      {/* 2. Fullscreen Lyrics View */}
      {isFullScreen && (
        <div
          className='mnp-lyrics-fullscreen-modal'
          style={{
            background: `linear-gradient(180deg, ${dominantColor} 0%, #121212 95%)`,
          }}
        >
          {/* Top Header */}
          <div className='mnp-lyrics-fs-header'>
            <button
              className='mnp-lyrics-fs-close-btn'
              onClick={(e) => {
                e.stopPropagation();
                setIsFullScreen(false);
              }}
              aria-label='Close lyrics'
            >
              <svg role='img' height='24' width='24' viewBox='0 0 24 24' fill='#ffffff'>
                <path d='M2.97 5.47a.75.75 0 0 1 1.06 0L12 13.44l7.97-7.97a.75.75 0 1 1 1.06 1.06l-8.5 8.5a.75.75 0 0 1-1.06 0l-8.5-8.5a.75.75 0 0 1 0-1.06z' />
              </svg>
            </button>

            <div className='mnp-lyrics-fs-title-box'>
              <span className='mnp-lyrics-fs-song'>{song.name}</span>
              <span className='mnp-lyrics-fs-artist'>
                {song.artists?.map((a) => a.name).join(', ')}
              </span>
            </div>

            {/* Right: 3-Dots Options Button */}
            <div className='mnp-lyrics-fs-settings-wrapper' ref={settingsRef}>
              <button
                className='mnp-lyrics-fs-settings-btn'
                onClick={(e) => {
                  e.stopPropagation();
                  setShowSettings(!showSettings);
                }}
                aria-label='Lyrics options'
              >
                <svg role='img' height='24' width='24' viewBox='0 0 24 24' fill='currentColor'>
                  <path d='M4.5 10.5C3.67 10.5 3 11.17 3 12s.67 1.5 1.5 1.5S6 12.83 6 12s-.67-1.5-1.5-1.5zm7.5 0c-.83 0-1.5.67-1.5 1.5s.67 1.5 1.5 1.5 1.5-.67 1.5-1.5-.67-1.5-1.5-1.5zm7.5 0c-.83 0-1.5.67-1.5 1.5s.67 1.5 1.5 1.5 1.5-.67 1.5-1.5-.67-1.5-1.5-1.5z' />
                </svg>
              </button>

              {showSettings && (
                <div 
                  className='mnp-lyrics-fs-settings-popover'
                  onClick={(e) => e.stopPropagation()}
                >
                  <div className='mnp-lyrics-setting-row'>
                    <span>Sync lyrics to music</span>
                    <label className='mnp-custom-switch'>
                      <input
                        type='checkbox'
                        checked={followLyrics}
                        onChange={(e) => {
                          const checked = e.target.checked;
                          setFollowLyrics(checked);
                          localStorage.setItem('lyrics_sync_enabled', String(checked));
                          if (checked) setIsUserScrolled(false);
                        }}
                      />
                      <span className='mnp-custom-slider' />
                    </label>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Scrolling Lyrics Area */}
          <div
            className='mnp-lyrics-fs-scroll'
            ref={fullScreenContainerRef}
            onScroll={handleScroll}
            onTouchMove={() => {
              if (followLyrics && lyricsData?.synced && !isUserScrolled) {
                setIsUserScrolled(true);
              }
            }}
          >
            {lyricsData.lines.map((line, idx) => {
              const isCurrent = idx === activeIndex;
              const isPassed = lyricsData.synced && followLyrics && activeIndex !== -1 && idx < activeIndex;
              const displayText = (showRomanized && line.romanizedText) ? line.romanizedText : line.text;
              return (
                <p
                  key={idx}
                  ref={isCurrent ? activeLineRef : null}
                  className={`mnp-lyrics-fs-line ${isCurrent ? 'active' : ''} ${isPassed ? 'passed' : ''}`}
                  onClick={() => {
                    if (lyricsData?.synced && typeof line.timeMs === 'number' && !isNaN(line.timeMs) && line.timeMs >= 0) {
                      playerService.seekToPosition(line.timeMs).then();
                      setIsUserScrolled(false);
                    }
                  }}
                >
                  {displayText}
                </p>
              );
            })}
          </div>

          {/* Floating Sync Button */}
          {followLyrics && lyricsData?.synced && isUserScrolled && (
            <button
              className='mnp-lyrics-fs-sync-btn'
              onClick={(e) => {
                e.stopPropagation();
                setIsUserScrolled(false);
                if (activeLineRef.current) {
                  activeLineRef.current.scrollIntoView({
                    behavior: 'smooth',
                    block: 'center',
                  });
                }
              }}
            >
              <EqualizerIcon />
              <span>Sync</span>
            </button>
          )}

          {/* Bottom Player Bar */}
          <div className='mnp-lyrics-fs-footer'>
            <div className='mnp-lyrics-fs-seekbar'>
              <Slider
                isEnabled={true}
                value={sliderValue}
                onChange={(val) => {
                  setIsSeeking(true);
                  setSliderValue(val);
                }}
                onChangeEnd={handleSeekEnd}
              />
              <div className='mnp-lyrics-fs-time'>
                <span>{msToTime(sliderValue * duration)}</span>
                <span>{msToTime(duration)}</span>
              </div>
            </div>

            <div className='mnp-lyrics-fs-controls'>
              <button
                className='mnp-lyrics-fs-icon-btn'
                onClick={handleShare}
                aria-label='Share'
              >
                <svg role='img' height='22' width='22' viewBox='0 0 24 24' fill='#ffffff'>
                  <path d='M18 16.08c-.76 0-1.44.3-1.96.77L8.91 12.7c.05-.23.09-.46.09-.7s-.04-.47-.09-.7l7.05-4.11c.54.5 1.25.81 2.04.81 1.66 0 3-1.34 3-3s-1.34-3-3-3-3 1.34-3 3c0 .24.04.47.09.7L8.04 9.81C7.5 9.31 6.79 9 6 9c-1.66 0-3 1.34-3 3s1.34 3 3 3c.79 0 1.5-.31 2.04-.81l7.12 4.16c-.05.21-.08.43-.08.65 0 1.61 1.31 2.92 2.92 2.92s2.92-1.31 2.92-2.92c0-1.61-1.31-2.92-2.92-2.92z' />
                </svg>
              </button>

              <button
                className='mnp-lyrics-fs-play-btn'
                onClick={() => (paused ? playerService.startPlayback() : playerService.pausePlayback())}
                aria-label={paused ? 'Play' : 'Pause'}
              >
                {paused ? <Play /> : <Pause />}
              </button>

              {/* Bottom Right: English / Original Segmented Toggle */}
              {lyricsData?.hasRomanized ? (
                <div className='mnp-lyrics-script-segmented mnp-fs-bottom-segmented'>
                  <button
                    className={`mnp-segmented-chip ${showRomanized ? 'active' : ''}`}
                    onClick={(e) => {
                      e.stopPropagation();
                      setShowRomanized(true);
                      localStorage.setItem('lyrics_prefer_romanized', 'true');
                    }}
                  >
                    Eng
                  </button>
                  <button
                    className={`mnp-segmented-chip ${!showRomanized ? 'active' : ''}`}
                    onClick={(e) => {
                      e.stopPropagation();
                      setShowRomanized(false);
                      localStorage.setItem('lyrics_prefer_romanized', 'false');
                    }}
                  >
                    Org
                  </button>
                </div>
              ) : (
                <div style={{ width: 40 }} />
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
});

MobileLyricsCard.displayName = 'MobileLyricsCard';
export default MobileLyricsCard;
